import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { TextButton } from '@bibliocommons/base-buttons';
import { Link } from '@bibliocommons/base-links';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';
import { userIdentityShape } from '@bibliocommons/bc-prop-types';
import UserAvatar from '@bibliocommons/user-avatar';
import UserBadge from '@bibliocommons/user-badge';
import UserCardPopover from '@bibliocommons/user-card';

import './UserCard.scss';

const UserCard = ({
  hasAvatar,
  hasBadge,
  hasLibraryFullName,
  isFollowing,
  isIgnoring,
  isMobileApp,
  isSyncingFollowForTargetUser,
  isSyncingIgnoreForTargetUser,
  toggleIsFollowing,
  toggleIsIgnoring,
  ugcType,
  user
}) => {
  const { active: isActive, id: userId, homeLibraryFullName, name, webUrl } = user?.toJS();

  const profileUrl = webUrl || `/user_profile/${userId}`;
  const hasProfileLink = !isMobileApp && Boolean(profileUrl);

  return (
    <div className="cp-core-user-card">
      {hasAvatar && (
        <div className="avatar-container">
          <UserAvatar hideLink={!hasProfileLink} size="xs" userIdentity={user} />
        </div>
      )}
      <div className="details">
        <div className="name-and-badge">
          <div className="popover-container">
            {!isActive && name}
            {isActive && (
              <UserCardPopover
                isFollowing={isFollowing}
                isIgnoring={isIgnoring}
                isMobileApp={isMobileApp}
                isSyncingFollowingState={isSyncingFollowForTargetUser}
                isSyncingIgnoringState={isSyncingIgnoreForTargetUser}
                offset={[0, -4]}
                toggleIsFollowing={toggleIsFollowing}
                toggleIsIgnoring={toggleIsIgnoring}
                userIdentity={user}
              >
                {hasProfileLink ? (
                  <Link dataKey="cp-core-user-card-profile-link" href={profileUrl}>
                    <span aria-hidden>{name}</span>
                    <ScreenReaderMessage>
                      <FormattedMessage id="sr_view_profile" values={{ username: name }} />
                    </ScreenReaderMessage>
                  </Link>
                ) : (
                  <TextButton className="popover-trigger" dataKey="cp-core-user-card-popover-trigger">
                    <span aria-hidden>{name}</span>
                    <ScreenReaderMessage>
                      <FormattedMessage
                        defaultMessage={`Actions for ${name}'s profile, expands popover`}
                        id="sr_view_user_card"
                        values={{ username: name }}
                      />
                    </ScreenReaderMessage>
                  </TextButton>
                )}
              </UserCardPopover>
            )}
          </div>
          <UserBadge displayStaffBadge={hasBadge} ugcType={ugcType} userIdentity={user} />
        </div>
        {hasLibraryFullName && <div className="home-library">{homeLibraryFullName}</div>}
      </div>
    </div>
  );
};

UserCard.defaultProps = {
  hasAvatar: false,
  hasBadge: false,
  hasLibraryFullName: false,
  isMobileApp: false
};

UserCard.propTypes = {
  hasAvatar: PropTypes.bool,
  hasBadge: PropTypes.bool,
  hasLibraryFullName: PropTypes.bool,
  isFollowing: PropTypes.bool,
  isIgnoring: PropTypes.bool,
  isMobileApp: PropTypes.bool,
  isSyncingFollowForTargetUser: PropTypes.bool,
  isSyncingIgnoreForTargetUser: PropTypes.bool,
  toggleIsFollowing: PropTypes.func.isRequired,
  toggleIsIgnoring: PropTypes.func.isRequired,
  ugcType: PropTypes.oneOf(['comment', 'contentNotice', 'list', 'quotation', 'summary', 'video']).isRequired,
  user: userIdentityShape.isRequired
};

export default UserCard;
